import { z } from 'zod';
import { canonicalize } from '../hashing/canonical.js';
import { personAttributeSchema, personTagSchema, profileKeySchema } from '../requirements/index.js';
import { lifecycleStateSchema } from './lifecycle.js';
import { personLanguagesSchema } from './languages.js';
import { effectiveOnSchema, lifecycleDetailSchema, MAX_STATUS_NOTE_LENGTH, statusDetailIssues, statusReasonSchema } from './status-reasons.js';
import { startOfDayInAgencyZone } from '../time/agency-dates.js';
import { emergencyContactSchema, employeeAssignmentSchema, servicePlacesSchema, emrProvisioningInputSchema, MAX_EMERGENCY_CONTACTS, emrProvisioningSchema, finalHrClearanceSchema, personAddressSchema, personIdentitySchema, personnelDateSchema, storedPersonIdentitySchema, } from './details.js';
export * from './details.js';
/**
 * A person — candidate or employee, one record either way (ADR-006, PLAN §5).
 *
 * PII rules that shape this schema (CLAUDE.md rule 8): the id is opaque and generated, so
 * it can appear in a URL; nothing derived from a name or email may be. Real person data
 * never appears in fixtures, tests, logs or screenshots.
 */
/**
 * Opaque, generated, and never derived from anything about the person. A slug of the name
 * would put PII in every URL that carries an id.
 */
export const personIdSchema = z.string().uuid();
/**
 * The closed attribute set, written out rather than derived from `personAttributeSchema`,
 * because `z.record` over an enum key infers every key as REQUIRED — which would force
 * callers to answer every question just to save a name, destroying the "unanswered"
 * state the whole tri-state design depends on.
 *
 * `.strict()` rejects an unknown attribute rather than silently keeping it, and
 * `tests/person-model.test.ts` asserts these keys still match the vocabulary, so the two
 * lists cannot drift.
 */
export const personAttributesSchema = z
    .object({
    'occupational-blood-exposure': z.string().min(1).optional(),
    'transports-patients': z.string().min(1).optional(),
    'requires-respirator': z.string().min(1).optional(),
    'provides-patient-services': z.string().min(1).optional(),
    'qualified-via-aide-registry': z.string().min(1).optional(),
    'has-conviction-disclosure': z.string().min(1).optional(),
    'had-exposure-event': z.string().min(1).optional(),
    'requires-fitness-for-duty': z.string().min(1).optional(),
    'holds-professional-licence': z.string().min(1).optional(),
    'provides-direct-care': z.string().min(1).optional(),
    'designated-aide-trainer': z.string().min(1).optional(),
    'delivers-medicaid-services': z.string().min(1).optional(),
})
    .strict();
/** Contact details. Field staff have no Vigeo address, so this is a personal one. */
export const personContactSchema = z.object({
    email: z.string().email(),
    phone: z.string().min(7).nullable().default(null),
    homePhone: z.string().trim().min(7).max(50).nullable().optional(),
    alternatePhone: z.string().trim().min(7).max(50).nullable().optional(),
    preferredCallTime: z.string().trim().max(200).nullable().optional(),
    address: personAddressSchema.nullable().optional(),
    emergencyContacts: z.array(emergencyContactSchema).max(MAX_EMERGENCY_CONTACTS).optional(),
}).strict();
export const personNameSchema = z.object({
    givenName: z.string().min(1),
    familyName: z.string().min(1),
    /** What they are actually called; used wherever a person is addressed. */
    preferredName: z.string().default(''),
    middleName: z.string().trim().max(200).optional(),
});
/**
 * How a person is written wherever a human reads their name.
 *
 * Defined once because it was already written out identically in three components, and the
 * review queue needed a fourth — server-side, where a divergent copy would have shown a
 * reviewer a different name from the one on the person's own page.
 */
export function personDisplayName(name) {
    return `${name.preferredName || name.givenName} ${name.familyName}`.trim();
}
/**
 * One interviewer, as the Add candidate form records them (#258, frame 06b `289:152`).
 *
 * `userId` IS THE POINT. Julio on the 2026-09-22 call: interviewers are people with ids rather
 * than free text, so an interview can be linked to an Outlook account later. There is no staff
 * directory to pick from yet — `app/me` is the only user endpoint the API has — so the id is
 * nullable: the signed-in HR user is recorded with their real id, and anyone else is a name
 * carried until a directory exists and can fill it in. Nullable rather than absent so the shape
 * does not change when that happens.
 */
export const interviewerSchema = z.object({
    userId: z.string().trim().min(1).max(200).nullable().default(null),
    name: z.string().trim().min(1).max(200),
}).strict();
/**
 * How this candidacy started: the interview it begins after, and where they came from.
 *
 * Separate from `identity` and `assignment` because it is neither — it is provenance, captured
 * once at the point HR adds the person, and it is what the frame's Interview section writes.
 * Optional, so every person recorded before this existed still parses.
 */
/**
 * An interview date the TIMELINE can actually place, which is stricter than a valid calendar date.
 *
 * `buildTimelinePage` converts this field with `startOfDayInAgencyZone` at READ time — the first
 * read-time caller of that helper in the codebase; every other caller converts at write time, where
 * a bad value fails the write and never persists. The helper throws for every day before
 * 1883-11-19, because `America/New_York` ran on LMT (-04:56:02) until then and the offset resolves
 * in whole minutes.
 *
 * Without this refinement a mistyped year — `0026-05-05` for `2026-05-05`, which a date field makes
 * easy — was accepted by `createPersonSchema`, stored with a 201, and then threw on EVERY
 * subsequent `GET /api/people/{id}/timeline`. Not just the interview row: the whole History tab for
 * that person, permanently, with no UI path to fix it because the intake card is read-only.
 *
 * Guarded at the write so it cannot be stored, AND survived at the read (see `timeline.ts`) so that
 * anything already stored, or arriving by some path this schema does not cover, costs one row
 * instead of the page.
 */
const interviewDateSchema = personnelDateSchema.refine((value) => {
    try {
        startOfDayInAgencyZone(value);
        return true;
    }
    catch {
        return false;
    }
}, 'Enter an interview date from 1883 onwards');
/**
 * WHERE A CANDIDATE CAME FROM, as a closed list (#341, Paul Rios via Adrian, 2026-09-28).
 *
 * The four named sources are the ones the agency advertises on, and there is deliberately NO
 * referral option: Adrian confirmed the agency does not track staff referrals, so an option for it
 * would collect a number nobody owns.
 *
 * IDS, NEVER LABELS, are what gets stored. Reporting is the entire reason this stopped being a text
 * box — "Indeed", "indeed" and "Indeed.com" are three rows in a count of one source — and a label
 * is a thing design may reword ("LinkedIn" → "LinkedIn Jobs") without meaning to re-key history.
 */
export const candidateSourceSchema = z.enum(['indeed', 'linkedin', 'instagram', 'facebook', 'other']);
/** What HR reads for each stored id. The dropdown's order is `candidateSourceSchema.options`. */
export const candidateSourceLabels = {
    indeed: 'Indeed',
    linkedin: 'LinkedIn',
    instagram: 'Instagram',
    facebook: 'Facebook',
    other: 'Other',
};
export const candidateIntakeSchema = z.object({
    /**
     * A calendar date, not an instant: an interview happened on a day, in the agency's zone.
     * `personnelDateSchema` rather than a regex of its own — a regex accepts 2026-13-45 — plus the
     * agency-zone refinement above, because this one is converted on read.
     */
    interviewedOn: interviewDateSchema.nullable().default(null),
    interviewers: z.array(interviewerSchema).max(10).default([]),
    /**
     * READ STAYS WIDE ON PURPOSE, exactly as `hrWorkflowSettingsSchema.defaultOfferTarget` does and
     * for the reason its comment gives: people are already stored with free text here, from the
     * months this field WAS a text box. Narrowing this to `candidateSourceSchema` would make those
     * documents fail to PARSE — taking the whole person record with it, so their page, their
     * checklist and their History would all break. That is a worse failure than the one being fixed.
     *
     * The WRITE is narrowed instead (`writableCandidateIntakeSchema` below), so a value outside the
     * five ids cannot be newly stored.
     */
    foundUsThrough: z.string().trim().min(1).max(200).nullable().default(null),
    /**
     * What HR typed when they picked "Other". Nullable with a default, so every intake written
     * before this field existed still parses: the key is simply absent and the default applies —
     * `.strict()` rejects unknown keys, never missing ones.
     */
    foundUsThroughOther: z.string().trim().min(1).max(200).nullable().default(null),
}).strict();
/**
 * WHAT MAY BE WRITTEN, as opposed to what may be read — the same split as
 * `writableHrWorkflowSettingsSchema` in `hr-workflow/model.ts`, whose comment explains the
 * reasoning, and for the same reason: reads must survive what is already stored, writes must not
 * add more of it.
 *
 * Three rules, and each of them has a way of being got wrong:
 *  - the source must be one of the five ids, so a report can group on it;
 *  - "other" must carry the typed text, or it records that HR answered and says nothing;
 *  - anything but "other" must carry no text, so a later edit cannot leave "indeed" sitting beside
 *    a stale "a friend at church".
 */
export const writableCandidateIntakeSchema = candidateIntakeSchema.superRefine((intake, context) => {
    if (intake.foundUsThrough !== null && !candidateSourceSchema.safeParse(intake.foundUsThrough).success) {
        context.addIssue({ code: 'custom', path: ['foundUsThrough'], message: 'Choose one of the listed sources.' });
        return;
    }
    if (intake.foundUsThrough === 'other') {
        if (intake.foundUsThroughOther === null) {
            context.addIssue({ code: 'custom', path: ['foundUsThroughOther'], message: 'Say where they found us.' });
        }
        return;
    }
    if (intake.foundUsThroughOther !== null) {
        context.addIssue({ code: 'custom', path: ['foundUsThroughOther'], message: 'Only "Other" carries typed text.' });
    }
});
/**
 * The one line a human reads for "Found us through", from whatever is stored.
 *
 * Three cases, because the read is wide: one of the five ids becomes its label, "other" becomes
 * what HR typed, and a value stored before the list existed is free text and is shown as it was
 * written. Never the raw id — "indeed" is a key, not a sentence.
 */
export function foundUsThroughLabel(intake) {
    const source = intake.foundUsThrough;
    if (source === null)
        return null;
    const known = candidateSourceSchema.safeParse(source);
    if (!known.success)
        return source;
    if (known.data === 'other')
        return intake.foundUsThroughOther ?? candidateSourceLabels.other;
    return candidateSourceLabels[known.data];
}
/**
 * A person AS A BROWSER RECEIVES ONE (#606). `identity` is the response shape, which has no date of
 * birth. The API's data layer reads and writes `storedPersonSchema` below, and every HR response
 * passes through `toPersonResponse` on its way out.
 */
export const personSchema = z.object({
    id: personIdSchema,
    /**
     * The Cosmos partition key, and always equal to `id`: a person's whole record is one
     * partition, so every read of their file is single-partition (infra/modules/cosmos.bicep).
     */
    personId: personIdSchema,
    docType: z.literal('person'),
    name: personNameSchema,
    contact: personContactSchema,
    lifecycle: lifecycleStateSchema,
    /** Decision 3: roles are configurable JobProfiles, and a person may hold more than one. */
    jobProfileKeys: z.array(profileKeySchema).default([]),
    /** Contractor and Driver are tags, never profiles — nobody is duplicated as "RN (driving)". */
    tags: z.array(personTagSchema).default([]),
    /** Absent key = unanswered, which is what makes a trigger resolve to `unknown`. */
    attributes: personAttributesSchema.default({}),
    hireDate: z.string().datetime().nullable().default(null),
    /** Assigned at conversion (PLAN Phase 3); null for everyone still in the pipeline. */
    employeeId: z.string().min(1).nullable().default(null),
    /** How this candidacy started — the interview and the source (#258). */
    intake: candidateIntakeSchema.optional(),
    /**
     * Set only by the "add existing staff" action (ADR-016, #459): this person was added directly as
     * Active, with no candidacy and no offer. Provenance, never cleared. Optional, so every record
     * written before it existed still parses and there is no migration. Deliberately absent from
     * `createPersonSchema` and `updatePersonSchema`: nothing but the staff action may write it.
     */
    staffSource: z.literal('existing-staff').optional(),
    /** Additive current HR details; earlier records and original applications stay intact. */
    identity: personIdentitySchema.optional(),
    assignment: employeeAssignmentSchema.optional(),
    emr: emrProvisioningSchema.optional(),
    finalHrClearance: finalHrClearanceSchema.nullable().optional(),
    /**
     * Languages spoken, picked from a closed list (#772). Optional, so every record written before it existed
     * still parses. Edited through the sparse update; a person added by hand may have it.
     */
    languages: personLanguagesSchema.optional(),
    /**
     * The reason, note and effective date of the LATEST status change (#772). Server-owned: written only by the
     * lifecycle route and absent from every create and update schema, so a PATCH cannot set it. `null` after a
     * change that had nothing to hold; absent on a record from before this existed.
     */
    lifecycleDetail: lifecycleDetailSchema.nullable().optional(),
    createdAt: z.string().datetime(),
    createdBy: z.string().min(1),
    updatedAt: z.string().datetime(),
    updatedBy: z.string().min(1),
});
/**
 * A person AS STORED. The same record, with the identity details the store still holds
 * (`storedPersonIdentitySchema`). For `apps/api` and the merge below; never a response.
 */
export const storedPersonSchema = personSchema.extend({ identity: storedPersonIdentitySchema.optional() });
const RESPONSE_IDENTITY_KEYS = Object.keys(personIdentitySchema.shape);
/**
 * THE projection from a stored person to the one a browser may have (#606).
 *
 * It KEEPS the identity keys the response schema names instead of removing one by name, so a
 * stored-only detail added later is left out without anyone remembering to list it here. Every
 * other key passes through untouched, which is what lets the lifecycle route's `issued` and
 * `closed` ride on the same object.
 */
export function toPersonResponse(stored) {
    const { identity, ...rest } = stored;
    if (typeof identity !== 'object' || identity === null || Array.isArray(identity))
        return rest;
    const kept = Object.entries(identity).filter(([key]) => RESPONSE_IDENTITY_KEYS.includes(key));
    return { ...rest, identity: Object.fromEntries(kept) };
}
/**
 * What a client may send when creating a person. Server-owned fields — ids, timestamps,
 * the acting user, and `employeeId`, which only conversion may set — are deliberately
 * absent rather than optional, so a client cannot supply one and be quietly ignored.
 */
export const createPersonSchema = z.object({
    name: personNameSchema,
    contact: personContactSchema,
    jobProfileKeys: z.array(profileKeySchema).default([]),
    tags: z.array(personTagSchema).default([]),
    attributes: personAttributesSchema.default({}),
    hireDate: z.string().datetime().nullable().default(null),
    /** The NARROW one: this is the path the Add candidate form writes through (#341). */
    intake: writableCandidateIntakeSchema.optional(),
    /** The STORED shape: this is the existing write path for the plain date of birth, unchanged by #606. */
    identity: storedPersonIdentitySchema.optional(),
    assignment: employeeAssignmentSchema.optional(),
    emr: emrProvisioningInputSchema.optional(),
}).strict();
/**
 * What the "Add staff member" action sends (ADR-016, #459): the minimum a roster needs.
 *
 * Email is required and phone is optional, as the candidate form has it. The service area is the
 * existing `assignment.serviceArea`, one typed value; blank is sent as `null`, not as an empty string.
 * No SSN, no date of birth, no role or job profile and no first day: those belong to onboarding,
 * which is connected later. Strict, so a caller cannot slip `lifecycle`, `staffSource` or anything
 * else in beside it.
 */
export const createStaffMemberSchema = z.object({
    name: personNameSchema,
    contact: personContactSchema.pick({ email: true, phone: true }).strict(),
    serviceArea: z.string().trim().min(1).max(500).nullable().default(null),
    servicePlaces: servicePlacesSchema.optional(),
}).strict();
/**
 * Whether this person's compliance is tracked in the app (ADR-016 §4).
 *
 * Staff added directly are not, until onboarding is connected, so they read "Not tracked yet" and
 * are left out of the readiness counts. Everyone else is. This is the one place that says so; a
 * screen that counts readiness asks here rather than testing the marker itself.
 */
export function isComplianceTracked(person) {
    return person.staffSource === undefined;
}
/**
 * Whether this move back to Active skips the activation check (ADR-016 Amendment 2, decision C, #810).
 *
 * Only a person added by hand coming back from leave: they were added as Active without the check
 * (§1), and with no tracked compliance they could never pass it, so leave would be a one-way door.
 * The move records no final HR clearance and leaves the employee ID alone. Anyone else entering Active
 * keeps the full check, and so does a person added by hand who was rehired and is coming out of
 * onboarding, since the marker never clears.
 */
export function returnsWithoutActivationCheck(person, to) {
    return person.lifecycle === 'inactive' && to === 'active' && !isComplianceTracked(person);
}
/**
 * A SPARSE edit. `lifecycle` is NOT here: a state change is its own request with its own
 * guard, because a PATCH that silently accepts a lifecycle field is a conversion nobody
 * reviewed.
 *
 * Derived from `createPersonSchema` so every field is still defined exactly once
 * (CLAUDE.md rule 3). The only thing added is that `name` and `contact` may arrive
 * PARTIALLY filled, because they are groups of independent scalars: an edit that carries a
 * family name is not thereby an instruction to erase the preferred name.
 *
 * The contract, and the whole point of the type: an ABSENT key means "this edit does not
 * speak to that field"; a PRESENT key means "set it to exactly this". `.partial()` also
 * disarms the `.default()` on every field, which is what makes absence survivable —
 * `createPersonSchema` materialises `attributes: {}` and `hireDate: null` for a CREATE,
 * and those defaults must never reach an UPDATE (issue #100).
 */
export const updatePersonSchema = createPersonSchema.partial().extend({
    // `.partial()` only disarms TOP-LEVEL defaults, so without this the inner `.default(null)`
    // and `.default([])` still materialise and an edit naming one intake field erases the other
    // two. Nullable so the group can also be cleared outright.
    //
    // DELIBERATELY THE WIDE SCHEMA, unlike the create above. A sparse patch cannot be judged against
    // the source rules: `{"intake":{"foundUsThrough":"other"}}` is legal under the merge contract
    // when the stored record already carries the typed text, and `{"intake":{"foundUsThroughOther":
    // "a friend"}}` is legal when the stored source is already "other" — so a cross-field refinement
    // here would refuse valid edits while still not seeing the merged result.
    //
    // So the rules are enforced on the MERGED record instead, in `updatePersonHandler`, which has it
    // before the write. An earlier version of this note deferred that guard to "whatever editor is
    // built for it"; a review then reached the handler directly and stored both an unlisted source
    // and the `indeed` + stale-text contradiction this schema forbids on create.
    intake: candidateIntakeSchema.partial().nullable().optional(),
    name: personNameSchema.partial().optional(),
    contact: personContactSchema.partial().extend({ address: personAddressSchema.partial().nullable().optional() }).optional(),
    identity: storedPersonIdentitySchema.partial().optional(),
    assignment: employeeAssignmentSchema.partial().optional(),
    emr: emrProvisioningInputSchema.partial().optional(),
    /**
     * REPLACED AS A WHOLE, never merged (it is not in `NESTED_GROUPS`), so the rules in `personLanguagesSchema` judge
     * exactly what will be stored. Not in `createPersonSchema`: nothing that creates a person asks for it.
     */
    languages: personLanguagesSchema.optional(),
});
/** How long the reason on an update may be. */
export const MAX_UPDATE_REASON_LENGTH = 500;
/**
 * The reason that may ride beside a `PATCH /api/people/{id}` (ADR-013 section 2, "Use the candidate's answer"). It goes to the
 * `person.updated` audit row and is never merged into the record. Every edit before it sent none, so it stays optional.
 *
 * ITS OWN SCHEMA, NOT AN EXTENSION OF `updatePersonSchema`. The edit schema names the plain date of birth and is on
 * `tests/response-schemas-sensitive-keys.test.ts`'s list of schemas that may; a second exported schema built on it would
 * name it too and need a list entry of its own. The body is read twice instead: once as this, and once as the patch. The patch
 * schema is strict and would REFUSE a `reason` key, so the handler (`updatePersonHandler`) removes it before the patch is read.
 */
export const updatePersonReasonSchema = z.object({
    reason: z.string().trim().min(1).max(MAX_UPDATE_REASON_LENGTH).optional(),
});
/**
 * `canonicalize` is the repo's single definition of "the same value": key order
 * irrelevant, array order significant. Reusing it here keeps "did this field move?" and
 * "did this document move?" answering the same question.
 */
// compared here comes out of `personSchema`, so it is a JSON scalar, array or plain
// object by construction; `canonicalize` throws loudly if that ever stops being true.
const sameValue = (a, b) => canonicalize((a ?? null)) === canonicalize((b ?? null));
/** These independent details merge recursively; arrays and trigger answers replace. */
const NESTED_GROUPS = ['name', 'contact', 'identity', 'assignment', 'emr', 'intake'];
function isObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function mergeDetails(before, patch) {
    const merged = { ...(isObject(before) ? before : {}) };
    for (const [key, value] of Object.entries(patch)) {
        if (value === undefined)
            continue;
        merged[key] = isObject(value) ? mergeDetails(merged[key], value) : value;
    }
    return merged;
}
/**
 * The dotted paths that clearing a whole group would remove — every field in it that holds a
 * value. Clearing a group that is already absent moves nothing, so a patch sending `null` for an
 * intake the person never had is not recorded as a change.
 */
function clearedPaths(before, prefix) {
    if (!isObject(before))
        return [];
    return Object.entries(before).flatMap(([key, value]) => {
        // An EMPTY ARRAY held nothing, so clearing it moved nothing. The form stores
        // `interviewers: []` whenever HR fills only the date or only the source, so without this a
        // later `{"intake": null}` wrote an audit event naming `intake.interviewers` for a person who
        // never had an interviewer — a diff claiming a field moved when it was empty either way.
        if (value === undefined || value === null || (Array.isArray(value) && value.length === 0))
            return [];
        const path = `${prefix}.${key}`;
        return isObject(value) ? clearedPaths(value, path) : [path];
    });
}
function detailChanges(before, patch, prefix) {
    const group = isObject(before) ? before : {};
    return Object.entries(patch).flatMap(([key, value]) => {
        if (value === undefined)
            return [];
        const path = `${prefix}.${key}`;
        if (isObject(value))
            return detailChanges(group[key], value, path);
        return sameValue(group[key], value) ? [] : [path];
    });
}
/**
 * Apply a sparse edit to a stored person.
 *
 * The ONE merge in the system, used by the PATCH route and by CSV import, because two
 * merges are two chances to get this wrong. A shallow `{ ...existing, ...patch }` is
 * exactly the defect in issue #100: it replaces `name` and `contact` wholesale, so an edit
 * that names only a family name silently clears the preferred name and the phone.
 *
 * `jobProfileKeys`, `tags` and `attributes` are REPLACED when present and are never merged
 * element by element. That is deliberate and load-bearing: absence of an attribute key IS
 * the unanswered state (see `personAttributesSchema`), so an element-wise merge would make
 * "not answered" unsettable and strand people at "Needs review" forever — the exact bug
 * the AttributeEditor was written to end.
 */
export function applyPersonPatch(existing, patch) {
    // String-keyed reads of a parsed object; every value stays `unknown` and is only moved,
    // never interpreted. `personSchema.parse` at the end is what re-establishes the type.
    const before = existing;
    const merged = { ...before };
    for (const [key, value] of Object.entries(patch)) {
        // An optional key that was not sent is absent rather than undefined (Zod 3), but a
        // hand-built patch could still carry one. Treat it as "not spoken to".
        if (value === undefined)
            continue;
        if (NESTED_GROUPS.includes(key)) {
            // An explicit null CLEARS the whole group, which is the one thing a field-by-field merge
            // cannot express. Absent means "not spoken to"; null means "remove it". Only `intake` is
            // nullable today; handling it here rather than at that one schema keeps the rule in the
            // place that would otherwise throw on it.
            if (value === null) {
                merged[key] = undefined;
                continue;
            }
            // Field by field. A wholesale replacement here is the defect: a patch naming only a
            // family name would clear the preferred name, and a CSV row without a phone column
            // would clear a stored phone number.
            merged[key] = mergeDetails(before[key], value);
            continue;
        }
        merged[key] = value;
    }
    // The STORED schema: the merge runs on a stored record and its result is written back, so the
    // response schema here would refuse (it is strict) any person who holds a stored-only detail.
    return storedPersonSchema.parse(merged);
}
/**
 * The dotted paths a patch would actually MOVE, sorted.
 *
 * Field names only, never their values — an audit event that copied the values would be a
 * second personnel file with none of its access controls (see `auditEventSchema`).
 *
 * This is a diff, not a list of the keys the caller happened to send. Before it existed,
 * every CSV import row recorded the same six field names whether or not anything changed,
 * so the audit trail could not answer the only question anyone asks of it after a bulk
 * import: what did that import actually do to this person?
 */
export function personPatchChangedFields(existing, patch) {
    // parsed object by string key; the values stay `unknown` and are only compared.
    const before = existing;
    const changed = [];
    for (const [key, value] of Object.entries(patch)) {
        // An optional key that was not sent is absent, not undefined (Zod 3), but a caller
        // that hand-builds a patch could still pass one. Treat it as "not spoken to".
        if (value === undefined)
            continue;
        if (NESTED_GROUPS.includes(key)) {
            // An explicit null CLEARS the group, the same meaning `applyPersonPatch` gives it. This ran
            // FIRST on the PATCH route, so without this clause `Object.entries(null)` threw before the
            // merge was ever reached and the caller got a 500 for a documented request.
            if (value === null) {
                changed.push(...clearedPaths(before[key], key));
                continue;
            }
            // reason as `before`: string-keyed read of a parsed object.
            changed.push(...detailChanges(before[key], value, key));
            continue;
        }
        if (!sameValue(before[key], value))
            changed.push(key);
    }
    return changed.sort();
}
export const changeLifecycleSchema = z.object({
    to: lifecycleStateSchema,
    /**
     * Recorded on the audit event; an unexplained state change is not reviewable.
     *
     * TRIMMED BEFORE THE LENGTH CHECK. `z.string().min(1)` accepted `"   "`, so the server took a
     * whitespace reason and wrote it to the audit trail — and a UI comment claimed this schema refused
     * one, which made the client's own gate look like a second line of defence when it was the only
     * one. Found by an adversarial review of #373 on 2026-09-30.
     *
     * `.trim()` also normalises what is stored, so a reason cannot differ from an identical one by
     * invisible padding.
     */
    reason: z.string().trim().min(1),
    confirmFinalHrClearance: z.boolean().optional(),
    /** Why, from the closed list for the state moved to: required for Inactive and Separated, refused for any other (#772). */
    statusReason: statusReasonSchema.optional(),
    /** An optional note on that reason. */
    statusNote: z.string().trim().min(1).max(MAX_STATUS_NOTE_LENGTH).optional(),
    /** The day the change actually took effect, when that is not the day it is recorded (#772). Optional on every move. */
    effectiveOn: effectiveOnSchema.optional(),
}).superRefine((change, context) => {
    for (const message of statusDetailIssues(change.to, change)) {
        context.addIssue({ code: 'custom', path: ['statusReason'], message });
    }
});
/** Everything the requirement resolver needs, projected off the person record. */
export function personFacts(person) {
    return {
        employerResponsibility: person.assignment?.employerResponsibility,
        employmentClassification: person.tags.includes('contractor') && person.assignment?.employmentClassification === 'w2' ? null : person.assignment?.employmentClassification,
        jobProfileKeys: person.jobProfileKeys,
        tags: person.assignment?.employmentClassification === '1099' ? [...new Set([...person.tags, 'contractor'])] : person.tags,
        attributes: person.attributes,
        // Filled by the evaluator once statuses exist; empty here means "not yet known",
        // which is exactly the `unknown` the tri-state design wants.
        requirementStatuses: {},
        recordTypesPresent: [],
    };
}
export { personAttributeSchema };
/** Missing or contradictory scope cannot be fixed by an attestation checkbox. */
export function personScopeIssues(person, knownProfiles) {
    const issues = [];
    if (!person.assignment?.employmentClassification)
        issues.push('Confirm employment classification.');
    if (!person.assignment?.employerResponsibility || !person.assignment.employerName?.trim())
        issues.push('Confirm the actual employer and responsibility for employment documents.');
    if (!person.assignment?.assignedDuties?.trim())
        issues.push('Record the actual assigned duties and service scope.');
    if (person.jobProfileKeys.length === 0 || person.jobProfileKeys.some((key) => !knownProfiles.includes(key)))
        issues.push('Resolve all actual job profiles; an unsupported role cannot inherit office clearance.');
    if (person.tags.includes('contractor') && person.assignment?.employmentClassification === 'w2')
        issues.push('Resolve the contractor tag and W2 classification conflict.');
    return issues;
}
//# sourceMappingURL=person.js.map